import { DatePipe }           from '@angular/common';
import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import {
	ContractForm,
	ContractStatusEnum,
	ContractTypeGroupEnum,
	FiskalContract,
	FiskalContractPropertiesLog,
	FiskalCurrentTenantContract
}                             from '@app/modules/contract/models/contract.model';
import {
	IContractHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState
}                             from '@core/interfaces/api.interface';
import { AbstractApiService } from '@http/abstract-api.service';
import { MapperService }      from '@http/mapper.service';
import { SanitizerService }   from '@http/sanitizer.service';
import { TransitionService }  from '@http/transition.service';
import { ModelTypeEnum }      from '@models/transition.model';
import {
	AdminContractBodyDTO,
	AdminContractsApiClient,
	ClsContractCreateBodyDTO,
	ContractCreateBodyDTO,
	ContractDTO,
	ContractLogPeriodEnumDTO,
	ContractTypeGroupEnumDTO,
	CreateContractRequestParams,
	GetContractByIdRequestParams,
	GetContractsRequestParams as AdminGetContractsRequestParams,
	UpdateContractRequestParams as UpdateAdminContractRequestParams
}                             from '@openApiGenerated/admin-api';
import {
	ContractBodyDTO,
	ContractsApiClient,
	GetContractsRequestParams,
	TenantContractDTO,
	UpdateContractRequestParams
}                             from '@openApiGenerated/management-api';
import { EmailDetail }        from '@shared/form-elements/form-email-settings/form-email-settings.model';
import { LoggerService }      from '@shared/services/logger.service';
import { DateUtils }          from '@utils/helpers';
import { Observable }         from 'rxjs';


@Injectable( {
	providedIn: 'root'
} )
export class FiskalContractApiService extends AbstractApiService {

	constructor(
		private readonly _adminContractApiClient: AdminContractsApiClient,
		private readonly _contractApiClient: ContractsApiClient,
		private readonly _datePipe: DatePipe,
		private readonly _mapperService: MapperService,
		private readonly _sanitize: SanitizerService,
		private readonly _transition: TransitionService,
		protected http: HttpClient,
		protected logger: LoggerService
	) {
		super();
	}


	// SANITIZER ######################################
	private sanitizeApiResponse( entity: FiskalContract, context = this ): void {
		if ( entity instanceof FiskalContract ) {
			entity.createdDate = context._sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.lastModifiedDate = context._sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );
			entity.contractPropertiesLog = entity?.contractPropertiesLog?.map( ( fiskalContractPropertiesLog: FiskalContractPropertiesLog ) => {
				const fromString: any = fiskalContractPropertiesLog?.from;
				const toString: any = fiskalContractPropertiesLog?.to;
				const contractTypeGroup = fiskalContractPropertiesLog.contractTypeGroup as unknown as ContractTypeGroupEnumDTO;
				const fccDataCenterContractTypeGroup = fiskalContractPropertiesLog.fccDataCenterContractTypeGroup as unknown as ContractTypeGroupEnumDTO;

				return {
					...fiskalContractPropertiesLog,
					contractTypeGroup: context._mapperService.contracts.fiskalTypeGroupDtoToTmo( contractTypeGroup ),
					fccDataCenterContractTypeGroup: context._mapperService.contracts.fiskalTypeGroupDtoToTmo( fccDataCenterContractTypeGroup ),
					from: DateUtils.yearMonthStringToDate( fromString ),
					to: DateUtils.yearMonthStringToDate( toString )
				};
			} );
		}
	}

	private sanitizeCurrentTenantContractApiResponse( entity: FiskalCurrentTenantContract, context = this ): void {
		if ( entity instanceof FiskalCurrentTenantContract ) {
			const fromString: any = entity?.contractPropertiesLog?.from;
			const toString: any = entity?.contractPropertiesLog?.to;

			entity.contractPropertiesLog.from = DateUtils.yearMonthStringToDate( fromString );
			entity.contractPropertiesLog.to = DateUtils.yearMonthStringToDate( toString );
		}
	}

	private sanitizeForApiRequest( dto: AdminContractBodyDTO, context = this ): void {
		if ( dto?.contractChangeLog ) {
			dto.contractChangeLog.from = context._datePipe.transform( dto.contractChangeLog?.from, 'yyyyMM' );

			dto.contractChangeLog.contractTypeGroup =
				context._mapperService.contracts.fiskalTypeGroupTmoToDto( dto.contractChangeLog.contractTypeGroup as unknown as ContractTypeGroupEnum );
			dto.contractChangeLog.fccDataCenterContractTypeGroup =
				context._mapperService.contracts.fiskalTypeGroupTmoToDto( dto.contractChangeLog.fccDataCenterContractTypeGroup as unknown as ContractTypeGroupEnum );

			if ( !dto.contractChangeLog.fccDataCenterEnabled ) {
				dto.contractChangeLog.contractedFccDataCenterErs = null;
			}
		}
	}

	// LIST-TYPE REQUESTS ########################################################
	public search( params?: IContractHttpParams, useAdminApi: boolean = false, throwError = false ): Observable<RequestCompleteListState<FiskalContract>> {
		this.logger.trace( 'pre-search call' );

		const requestParam: GetContractsRequestParams = {
			contractIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.contractIds ),
			contractMonth: params?.contractMonth,
			contractTypeGroup: this._mapperService.contracts.fiskalTypeGroupListTmoToDto( params?.contractTypeGroup ),
			customerName: params?.customerName,
			fccDataCenterContractTypeGroup: this._mapperService.contracts.fiskalTypeGroupListTmoToDto( params?.fccDataCenterContractTypeGroup ),
			fccDataCenterEnabled: params?.dataCenterEnabled,
			filter: params?.filter,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForArrayArgAndReturnAsSingleValue<string>( params?.search ),
			sort: params?.sort,
			status: this._mapperService.contracts.statusListTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray<ContractStatusEnum>( params?.status )
			)
		};

		const adminRequestParam: AdminGetContractsRequestParams = {
			...requestParam,
			contractLogPeriod: params.contractLogPeriod as unknown as ContractLogPeriodEnumDTO[],
			customerIdentifier: params?.customerIdentifier,
			projectNumber: params?.projectNumber
		};

		const req$ = useAdminApi
								 ? this._adminContractApiClient.getContracts( adminRequestParam )
								 : this._contractApiClient.getContracts( requestParam );

		return this._transition.handleListRequestResponse<ContractDTO, FiskalContract>(
			req$,
			FiskalContract,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	// ENTITY-TYPE REQUESTS ######################################################
	public read( id: string, useAdminApi: boolean = false, withFullResponse: boolean = true, throwError = false ):
		Observable<RequestCompleteEntityState<FiskalContract>> {
		this.logger.debug( 'pre-read call: ', id );
		const requestParam: GetContractByIdRequestParams = {
			contractId: id
		};

		const req$ = useAdminApi
								 ? this._adminContractApiClient.getContractById( requestParam, withFullResponse ? 'response' : undefined )
								 : this._contractApiClient.getContractById( requestParam, withFullResponse ? 'response' : undefined );

		return this._transition.handleEntityRequestResponse<ContractDTO | HttpResponse<ContractDTO>, FiskalContract>(
			req$,
			RequestActionEnum.READ,
			FiskalContract,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public create( fiskalContract: FiskalContract ): Observable<RequestCompleteEntityState<FiskalContract>> {
		this.logger.debug( 'pre-create call: ', { ...fiskalContract } );

		const contractForm: ContractForm = {
			contractChangeLog: { ...fiskalContract?.contractPropertiesLog[ 0 ] }, // new object reference is needed here
			customerIdentifier: fiskalContract?.customerIdentifier,
			customerName: fiskalContract?.customerName,
			id: fiskalContract?.id,
			notifications: fiskalContract?.notifications
		};

		const contractCreateBodyDTO = this._transition.prepareEntityRequest<ContractForm, ContractCreateBodyDTO>(
			contractForm,
			ClsContractCreateBodyDTO,
			this,
			this.sanitizeForApiRequest
		);

		const requestParam: CreateContractRequestParams = {
			contractCreateBodyDTO
		};

		const req$ = this._adminContractApiClient.createContract( requestParam );
		return this._transition.handleEntityRequestResponse<ContractDTO, FiskalContract>(
			req$,
			RequestActionEnum.CREATE,
			FiskalContract,
			this,
			this.sanitizeApiResponse
		);
	}


	public update(
		fiskalContract: FiskalContract,
		useAdminApi: boolean = false,
		withFullResponse: boolean = true
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		this.logger.trace( 'pre-update call: ', { ...fiskalContract } );

		const contractChangeLog = fiskalContract?.contractPropertiesLog?.length
															? { ...fiskalContract?.contractPropertiesLog[ 0 ] }
															: null;

		const contractBodyDTO: ContractBodyDTO = {
			notifications: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( fiskalContract.notifications )
			?.map( ( emailDetail: EmailDetail ) => {
				return {
					...emailDetail,
					language: this._mapperService.contracts.fiskalEmailDetailLanguageTmoToDto( emailDetail.language )
				};
			} )
		};

		let req$;
		if ( useAdminApi ) {
			const adminContractBodyDTO: AdminContractBodyDTO = {
				contractChangeLog: contractChangeLog ? {
					businessPremisesLimit: contractChangeLog.businessPremisesLimit,
					contractTypeGroup: contractChangeLog.contractTypeGroup as unknown as ContractTypeGroupEnumDTO,
					contractedBusinessPremises: contractChangeLog.contractedBusinessPremises,
					contractedCloudErs: contractChangeLog.contractedCloudErs,
					contractedFccDataCenterErs: contractChangeLog.contractedFccDataCenterErs,
					contractedTokenErs: contractChangeLog.contractedTokenErs,
					fccDataCenterContractedBusinessPremises: contractChangeLog.fccDataCenterContractedBusinessPremises,
					fccDataCenterContractTypeGroup: contractChangeLog.fccDataCenterContractTypeGroup as unknown as ContractTypeGroupEnumDTO,
					fccDataCenterEnabled: contractChangeLog.fccDataCenterEnabled,
					fccDataCenterVolume: contractChangeLog.fccDataCenterVolume,
					from: contractChangeLog.from as unknown as string,
					projectNumber: contractChangeLog.projectNumber,
					volume: contractChangeLog.volume
				} : undefined,
				customerName: fiskalContract.customerName,
				notifications: contractBodyDTO.notifications
			};

			const adminRequestParam: UpdateAdminContractRequestParams = {
				adminContractBodyDTO,
				contractId: fiskalContract?.id,
				ifMatch: fiskalContract?.etag
			};

			this.sanitizeForApiRequest( adminContractBodyDTO );
			req$ = this._adminContractApiClient.updateContract( adminRequestParam, withFullResponse ? 'response' : undefined );
		} else {
			const customerRequestParam: UpdateContractRequestParams = {
				contractBodyDTO,
				contractId: fiskalContract?.id,
				ifMatch: fiskalContract?.etag
			};

			req$ = this._contractApiClient.updateContract( customerRequestParam, withFullResponse ? 'response' : undefined );
		}


		return this._transition.handleEntityRequestResponse<ContractDTO | HttpResponse<ContractDTO>, FiskalContract>(
			req$,
			RequestActionEnum.UPDATE,
			FiskalContract,
			this,
			this.sanitizeApiResponse
		);
	}

	public delete( contractId: string, throwError = false ): Observable<RequestCompleteEntityState<FiskalContract>> {
		// this.logger.debug( 'pre-delete call: ', contractId );
		//
		// const requestParam: DeleteContractRequestParams = {
		// 	contractId
		// };
		// const req$ = this._contractApiClient.delete( requestParam, 'response' );
		// return this._transition.handleEntityRequestResponse<HttpResponse<any>, FiskalContract>(
		// 	req$,
		// 	RequestActionEnum.DELETE,
		// 	FiskalContract,
		// 	this,
		// 	null,
		// 	null,
		// 	null,
		// 	null,
		// 	throwError
		// );

		return null;
	}

	public currentTenantContract( throwError = false ): Observable<RequestCompleteEntityState<FiskalCurrentTenantContract>> {
		this.logger.debug( 'pre-read currentTenantContract' );

		const req$ = this._contractApiClient.getCurrentContract();

		return this._transition.handleEntityRequestResponse<TenantContractDTO | HttpResponse<TenantContractDTO>, FiskalCurrentTenantContract>(
			req$,
			RequestActionEnum.READ,
			FiskalCurrentTenantContract,
			this,
			this.sanitizeCurrentTenantContractApiResponse,
			null,
			null,
			null,
			throwError
		);
	}
}
